import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Sparkles,
  AlertTriangle,
  ShieldAlert,
  Scale,
  Wrench,
  Brain,
  Info,
  TrendingUp,
  RefreshCw,
  Play,
  Clock,
} from 'lucide-react';
import { SectionCard, EmptyState, StatusLevel, StatusBadge, KpiCardSkeleton, SectionSkeleton } from './DashboardWidgets';

// ── Types ─────────────────────────────────────────────────────
export interface AiInsight {
  severity?: string;
  title?: string;
  affected_repos?: string[];
  description?: string;
  recommendation?: string;
  risk_if_ignored?: string;
  impact_if_ignored?: string;
  category?: string;
  type?: string;
}

interface CrossAiInsightsProps {
  executiveSummary: string;
  insights: AiInsight[];
  loading?: boolean;
  error?: string;
  onRefetch?: () => void;
  refreshing?: boolean;
  lastAnalyzedAt?: string;
  isRefreshing?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────
function severityToStatus(severity: string | undefined): StatusLevel {
  const s = (severity || '').toLowerCase();
  if (s === 'critical' || s === 'high') return 'critical';
  if (s === 'medium' || s === 'moderate') return 'warning';
  return 'info';
}

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  security: <ShieldAlert size={14} />,
  compliance: <Scale size={14} />,
  'technical debt': <Wrench size={14} />,
  'ai generated': <Brain size={14} />,
};

const TYPE_LABELS: Record<string, string> = {
  systemic: 'Systemic',
  compound: 'Compound',
  misleading: 'Misleading Metric',
  trend: 'Trend',
  single_point_of_failure: 'Single Point of Failure',
};

// ── Component ─────────────────────────────────────────────────
export function CrossAiInsights({ executiveSummary, insights, loading, error, onRefetch, refreshing, lastAnalyzedAt, isRefreshing }: CrossAiInsightsProps) {
  const theme = useTheme();
  const [activeKpi, setActiveKpi] = useState<string | null>(null);

  // ── Compute severity counts for KPI cards ────────────────
  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, systemic: 0 };
    for (const ins of (insights || [])) {
      const sev = (ins.severity || '').toLowerCase();
      const typ = (ins.type || '').toLowerCase();
      if (sev === 'critical') c.critical++;
      else if (sev === 'high') c.high++;
      else if (sev === 'medium' || sev === 'moderate') c.medium++;
      if (typ === 'systemic') c.systemic++;
    }
    return c;
  }, [insights]);

  if (loading && (!insights || insights.length === 0)) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
          <Sparkles size={16} color={theme.palette.primary.main} />
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
            Generating AI Insights…
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} item xs={6} md={3}>
              <KpiCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <SectionSkeleton height={180} />
      </Stack>
    );
  }

  if (!insights || insights.length === 0) {
    return (
      <Stack spacing={2}>
        <EmptyState
          icon={<Sparkles size={24} />}
          title="No Cross AI Insights Yet"
          description={error
            ? `AI correlation could not generate insights: ${error}`
            : 'Run an AI KPI analysis with at least 2 repositories to generate cross-repo insights.'
          }
        />
        {onRefetch && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <Play size={16} />}
              onClick={onRefetch}
              disabled={refreshing || loading}
              disableElevation
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25, background: '#0B6BCB', '&:hover': { background: '#185ABC' }, '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 } }}
            >
              {refreshing ? 'Generating...' : 'Fetch Cross AI Insights'}
            </Button>
          </Box>
        )}
        {error && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#ef4444', 0.06), border: '1px solid', borderColor: alpha('#ef4444', 0.15) }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <AlertTriangle size={14} color="#ef4444" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                The AI LLM provider (Nexus) may not be configured on the scan-ai-backend. Verify that <code>NEXUS_BASE_URL_PROD</code> and <code>NEXUS_API_KEY_PROD</code> are set in the ECS task definition or Secrets Manager.
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
    );
  }

  const kpiDefs = [
    { id: 'critical', label: 'Critical', icon: <AlertTriangle size={14} />, count: counts.critical, color: '#ef4444' },
    { id: 'high', label: 'High', icon: <ShieldAlert size={14} />, count: counts.high, color: '#f97316' },
    { id: 'medium', label: 'Medium', icon: <Scale size={14} />, count: counts.medium, color: '#f59e0b' },
    { id: 'systemic', label: 'Systemic', icon: <TrendingUp size={14} />, count: counts.systemic, color: '#0B6BCB' },
  ];

  // ── Filter insights by active KPI card ───────────────────
  const sorted = [...insights].sort((a, b) => {
    const oa = SEVERITY_ORDER[(a.severity || 'info').toLowerCase()] ?? 5;
    const ob = SEVERITY_ORDER[(b.severity || 'info').toLowerCase()] ?? 5;
    return oa - ob;
  });

  const filteredInsights = activeKpi
    ? sorted.filter((ins) => {
        const sev = (ins.severity || '').toLowerCase();
        const typ = (ins.type || '').toLowerCase();
        return sev === activeKpi || typ === activeKpi;
      })
    : [];

  return (
    <Stack spacing={2}>
      {/* ═══════ ACTION BAR ═══════ */}
      {onRefetch && (
        <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {refreshing && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0B6BCB', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                <Typography sx={{ fontSize: 11, color: '#0B6BCB', fontWeight: 500 }}>Generating AI Insights...</Typography>
              </Stack>
            )}
            {!refreshing && lastAnalyzedAt && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled' }}>
                <Clock size={13} />
                <Typography sx={{ fontSize: 11 }}>{new Date(lastAnalyzedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
              </Stack>
            )}
            {isRefreshing && (
              <Chip
                icon={<CircularProgress size={10} sx={{ color: 'inherit' }} />}
                label="Updating..."
                size="small"
                sx={{
                  height: 22, fontSize: 10, fontWeight: 600,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  borderRadius: 1,
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
                }}
              />
            )}
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
            onClick={onRefetch}
            disabled={refreshing}
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 700, background: '#0B6BCB', '&:hover': { background: '#185ABC' }, '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 } }}
          >
            {refreshing ? 'Generating...' : 'Re-fetch AI Insights'}
          </Button>
        </Paper>
      )}

      {/* ═══════ ROW 1: Executive Summary + Insight Count ═══════ */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <SectionCard sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
              <Box sx={{ position: 'relative', width: 90, height: 90, mx: 'auto' }}>
                <svg width={90} height={90} viewBox="0 0 90 90">
                  <circle cx={45} cy={45} r={39} fill="none" stroke={alpha(theme.palette.divider, 0.3)} strokeWidth={8} />
                  <circle
                    cx={45} cy={45} r={39} fill="none"
                    stroke={counts.critical > 0 ? '#ef4444' : counts.high > 0 ? '#f59e0b' : '#22c55e'}
                    strokeWidth={8} strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 39}
                    strokeDashoffset={2 * Math.PI * 39 * (1 - Math.min(insights.length / Math.max(insights.length, 1), 1))}
                    transform="rotate(-90 45 45)"
                    style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                  />
                </svg>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 25, fontWeight: 800, color: counts.critical > 0 ? '#ef4444' : counts.high > 0 ? '#f59e0b' : '#22c55e', lineHeight: 1 }}>
                    {insights.length}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', fontWeight: 500 }}>
                    insights
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                AI Insights
              </Typography>
              <StatusBadge
                status={counts.critical > 0 ? 'critical' : counts.high > 0 ? 'warning' : 'success'}
                label={counts.critical > 0 ? 'Critical Issues' : counts.high > 0 ? 'Needs Attention' : 'Good'}
              />
            </Stack>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <SectionCard title="Executive Summary" icon={<Brain size={14} />} sx={{ flex: 1 }}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Sparkles size={16} color="#0B6BCB" style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.7 }}>
                  {executiveSummary || 'AI cross-repo correlation analysis complete. Click a severity card below to view insights.'}
                </Typography>
              </Stack>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ═══════ ROW 2: 4 KPI Cards (clickable, like tool tabs) ═══════ */}
      <Grid container spacing={2}>
        {kpiDefs.map((kpi) => {
          const isActive = activeKpi === kpi.id;
          const scoreColor = kpi.count > 0 ? kpi.color : '#22c55e';
          return (
            <Grid key={kpi.id} item xs={6} md={3}>
              <Box
                onClick={() => setActiveKpi(isActive ? null : kpi.id)}
                sx={{
                  p: 2, borderRadius: 2,
                  border: '2px solid', borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  cursor: 'pointer', transition: 'all 0.15s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.02) },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box sx={{ color: scoreColor, display: 'flex' }}>{kpi.icon}</Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{kpi.label}</Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: scoreColor }}>{kpi.count}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                  <StatusBadge
                    status={kpi.count === 0 ? 'success' : kpi.id === 'critical' ? 'critical' : kpi.id === 'high' ? 'critical' : 'warning'}
                    label={kpi.count === 0 ? 'Clear' : `${kpi.count} found`}
                  />
                  {kpi.count > 0 && <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>• details</Typography>}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* ═══════ ROW 3: Insight Details (shown on KPI card click) ═══════ */}
      {activeKpi && (
        <SectionCard title={`${kpiDefs.find(k => k.id === activeKpi)?.label || ''} — Insights & Remediation`}>
          {filteredInsights.length > 0 ? (
            <Stack spacing={1.5} sx={{ p: 2 }}>
              {filteredInsights.map((ins, idx) => {
                const sev = (ins.severity || 'medium').toLowerCase();
                const sevColor = sev === 'critical' ? '#ef4444' : sev === 'high' ? '#f97316' : sev === 'medium' ? '#f59e0b' : '#3b82f6';
                const catIcon = CATEGORY_ICONS[(ins.category || ins.type || '').toLowerCase()] || <Info size={14} />;
                return (
                  <Box
                    key={idx}
                    sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Chip
                        label={ins.severity || 'Medium'}
                        size="small"
                        sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(sevColor, 0.1), color: sevColor, borderRadius: 1 }}
                      />
                      {(ins.type || ins.category) && (
                        <Chip
                          icon={<Box sx={{ display: 'flex', color: 'inherit' }}>{catIcon}</Box>}
                          label={TYPE_LABELS[(ins.type || '').toLowerCase()] || ins.category || ins.type}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.55rem', fontWeight: 500 }}
                        />
                      )}
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{ins.title || 'Untitled Insight'}</Typography>
                    </Stack>
                    {ins.description && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
                        {ins.description}
                      </Typography>
                    )}
                    {ins.affected_repos && ins.affected_repos.length > 0 && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Affected:</Typography>
                        {ins.affected_repos.map((r) => (
                          <Chip key={r} label={r} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 500, bgcolor: alpha(sevColor, 0.08), color: sevColor }} />
                        ))}
                      </Stack>
                    )}
                    {ins.recommendation && (
                      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha('#0B6BCB', 0.04), border: `1px solid ${alpha('#0B6BCB', 0.12)}`, mt: 0.75 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                          <Sparkles size={10} color="#0B6BCB" />
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recommendation</Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: 'text.primary', lineHeight: 1.5 }}>{ins.recommendation}</Typography>
                      </Box>
                    )}
                    {(ins.risk_if_ignored || ins.impact_if_ignored) && (
                      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.04), border: `1px solid ${alpha('#ef4444', 0.12)}`, mt: 0.75 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                          <AlertTriangle size={10} color="#ef4444" />
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Impact if Ignored</Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: 'text.primary', lineHeight: 1.5 }}>{ins.impact_if_ignored || ins.risk_if_ignored}</Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12, color: 'text.disabled', py: 2, textAlign: 'center' }}>
              No insights found for this category. All clear.
            </Typography>
          )}
          <Typography
            onClick={() => setActiveKpi(null)}
            sx={{ fontSize: 11, color: 'primary.main', fontWeight: 600, mt: 1.5, mb: 1, ml: 2, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            ← Close details
          </Typography>
        </SectionCard>
      )}

      {/* ═══════ ROW 4: All Insights Summary (when no KPI selected) ═══════ */}
      {!activeKpi && (
        <SectionCard title={`All Insights (${insights.length})`} icon={<Sparkles size={14} />}>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            {sorted.map((ins, idx) => {
              const sev = (ins.severity || 'info').toLowerCase();
              const sevColor = sev === 'critical' ? '#ef4444' : sev === 'high' ? '#f97316' : sev === 'medium' ? '#f59e0b' : '#3b82f6';
              const catIcon = CATEGORY_ICONS[(ins.category || ins.type || '').toLowerCase()] || <Info size={14} />;
              return (
                <Box
                  key={idx}
                  sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Chip
                      label={ins.severity || 'Info'}
                      size="small"
                      sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(sevColor, 0.1), color: sevColor, borderRadius: 1 }}
                    />
                    {(ins.type || ins.category) && (
                      <Chip
                        icon={<Box sx={{ display: 'flex', color: 'inherit' }}>{catIcon}</Box>}
                        label={TYPE_LABELS[(ins.type || '').toLowerCase()] || ins.category || ins.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.55rem', fontWeight: 500 }}
                      />
                    )}
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{ins.title || 'Untitled Insight'}</Typography>
                  </Stack>
                  {ins.description && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {ins.description}
                    </Typography>
                  )}
                  {ins.affected_repos && ins.affected_repos.length > 0 && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.disabled' }}>Affected:</Typography>
                      {ins.affected_repos.map((r) => (
                        <Chip key={r} label={r} size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 500, bgcolor: alpha(sevColor, 0.08), color: sevColor }} />
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        </SectionCard>
      )}
    </Stack>
  );
}
